import { Card, Flex, Typography } from 'antd'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from 'src/components'

const { Title } = Typography

type Stat = {
  label: string
  value: string | number
}

type Props = {
  appsUsed: number
  sessions: number | undefined
  signIns30d: number | undefined
  /** Each metric is served by its own endpoint, so each follows its own code. */
  showAppsUsed?: boolean
  showSessions?: boolean
  showSignIns?: boolean
}

// A loading/unavailable metric renders an em-dash rather than a misleading 0.
const display = (value: number | undefined): string | number =>
  value === undefined ? '—' : value

export const UserQuickStats = ({
  appsUsed,
  sessions,
  signIns30d,
  showAppsUsed = true,
  showSessions = true,
  showSignIns = true
}: Props) => {
  const { t } = useTranslation()

  const stats: Stat[] = [
    ...(showSignIns
      ? [
          {
            label: t('users.detail.quickStats.signIns30d'),
            value: display(signIns30d)
          }
        ]
      : []),
    ...(showAppsUsed
      ? [
          {
            label: t('users.detail.quickStats.appsUsed'),
            value: appsUsed
          }
        ]
      : []),
    ...(showSessions
      ? [
          {
            label: t('users.detail.quickStats.sessions'),
            value: display(sessions)
          }
        ]
      : [])
  ]

  if (stats.length === 0) {
    return null
  }

  return (
    <Card size="small" styles={{ body: { padding: 0 } }}>
      <Flex>
        {stats.map((s, i) => (
          <Fragment key={s.label}>
            <Flex vertical gap={2} style={{ flex: 1, padding: 12 }}>
              <SectionLabel>{s.label}</SectionLabel>
              <Title
                level={4}
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {s.value}
              </Title>
            </Flex>
            {i < stats.length - 1 ? (
              <div
                style={{
                  width: 1,
                  background: 'var(--ant-color-border-secondary)'
                }}
              />
            ) : null}
          </Fragment>
        ))}
      </Flex>
    </Card>
  )
}
